import { getGuestToken, clearGuestToken } from './guestToken';
import type { OrderTimelineEvent } from './ordersApi';
import { toAdminPackageDetail } from '../types/packages';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

/**
 * Error thrown for non-2xx responses. Carries the HTTP status so callers can
 * branch on specific codes (e.g. 409 INVOICE_NOT_READY / INVOICE_ALREADY_EXISTS
 * on the Spec 051 invoice endpoints). `instanceof Error` still holds, so all
 * existing `err.message` handling is unaffected.
 */
export class ApiError extends Error {
  readonly status: number;
  /**
   * Stable machine code from the `{ code, message, details }` error body (e.g. Spec 059
   * `PROMO_VERSION_CONFLICT`), when the service sends one. Optional so every existing
   * `new ApiError(message, status)` keeps compiling.
   */
  readonly code?: string;
  readonly details?: Record<string, unknown>;
  /** Field → code map from the `{ message, code, errors }` envelope (e.g. #997 offer edits). */
  readonly errors?: Record<string, string>;
  /** The body's `message` as the server sent it (`message` may have field errors appended). */
  readonly serverMessage?: string;
  /**
   * #1113 — the Runtime Config key a 400/403 names (`PATCH /api/admin/dynamic-config`), so a card
   * can put the message on the field that caused it.
   */
  readonly key?: string;

  constructor(
    message: string,
    status: number,
    code?: string,
    details?: Record<string, unknown>,
    errors?: Record<string, string>,
    serverMessage?: string,
    key?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.errors = errors;
    this.serverMessage = serverMessage;
    this.key = key;
  }
}

// ── Admin Orders interfaces (F1 Phase 13) ──────────────────────────────────
export interface AdminOrderItem {
  id: string;
  orderNumber?: string;
  status: string;
  garageId: string;
  customerId: string;
  totalAmount: number;
  currency: string;
  deliveryMethod?: 'DELIVERY' | 'SELF_SERVICE';
  paymentId?: string;
  scheduledDate?: string;
  createdAt: string;
}

export interface AdminOrderDetail extends AdminOrderItem {
  bookingId?: string;
  vehicleId?: string;
  serviceOfferingId?: string;
  isSettled?: boolean;
  settlementId?: string;
  refundId?: string;
  refundEligible?: boolean;
  refundWindowExpiresAt?: string;
  notes?: string;
  completedAt?: string;
  updatedAt?: string;
  // Dispute fields (populated when status = DISPUTED)
  disputeReason?: string;
  disputeType?: string;
  disputeOpenedAt?: string;
  // Scheduling
  pickupTimeSlotId?: string;
  pickupTimeSlotLabel?: string | null;
  pickupAddressId?: string;
  durationDays?: number;
  // Relations
  timeline?: OrderTimelineEvent[];
  pickupDeliveries?: Array<{
    id: string;
    type: string;
    status: string;
    driverName: string | null;
    driverPhone: string | null;
    scheduledAt: string | null;
    completedAt: string | null;
    pickupAddress: string | null;
    deliveryAddress: string | null;
    notes: string | null;
  }>;
}

export interface PagedAdminOrders {
  content: AdminOrderItem[];
  totalPages: number;
  number: number;
  totalElements: number;
}

async function request<T>(method: string, url: string, data?: unknown, token?: string, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  // Logged-in requests (Bearer) are left byte-for-byte unchanged. Tokenless
  // requests attach a guest token so the gateway doesn't reject them with
  // 401 { "error": "guest_token_required" }.
  const isGuest = !token;
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    try {
      headers['X-Guest-Token'] = await getGuestToken();
    } catch {
      // Couldn't mint a guest token — proceed without it and let the real
      // request 401 naturally rather than crashing the app.
    }
  }

  const config: RequestInit = { method, headers, signal };
  if (data && method !== 'GET') config.body = JSON.stringify(data);

  // Single retry on TypeError (network / connection-reset, e.g. during an ECS
  // rolling deploy the prior HTTP/2 stream's backend died mid-flight). 400 ms
  // gives the browser time to open a fresh HTTP/2 connection. POST is excluded
  // because it's not safe to retry without idempotency guarantees.
  const shouldRetry = method !== 'POST';
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${url}`, config);
  } catch (err) {
    if (shouldRetry && err instanceof TypeError) {
      await new Promise(r => setTimeout(r, 400));
      response = await fetch(`${API_BASE_URL}${url}`, config);
    } else {
      throw err;
    }
  }

  // Guest-token 401 recovery: a stale/expired guest token yields
  // 401 { "error": "guest_token_required" }. Mint a fresh one and retry once.
  // Only for guest requests — the Bearer path is untouched. The body can be
  // read only once, so read text first, then parse.
  if (isGuest && response.status === 401) {
    const peeked = await response.text().catch(() => '');
    let isGuestTokenError = false;
    try {
      isGuestTokenError = JSON.parse(peeked)?.error === 'guest_token_required';
    } catch {
      isGuestTokenError = false;
    }
    if (isGuestTokenError) {
      clearGuestToken();
      try {
        headers['X-Guest-Token'] = await getGuestToken();
        response = await fetch(`${API_BASE_URL}${url}`, config);
      } catch {
        response = new Response(peeked || '', { status: 401 });
      }
    } else {
      response = new Response(peeked || '', {
        status: 401,
        statusText: response.statusText,
      });
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    let message = errorData.message || `HTTP error! status: ${response.status}`;
    if (errorData.errors && typeof errorData.errors === 'object') {
      const fieldErrors = Object.entries(errorData.errors)
        .map(([field, msg]) => `${field}: ${msg}`)
        .join('; ');
      if (fieldErrors) {
        message += ` (${fieldErrors})`;
      }
    }
    throw new ApiError(
      message,
      response.status,
      typeof errorData.code === 'string' ? errorData.code : undefined,
      errorData.details && typeof errorData.details === 'object' ? errorData.details : undefined,
      errorData.errors && typeof errorData.errors === 'object' ? errorData.errors : undefined,
      typeof errorData.message === 'string' ? errorData.message : undefined,
      typeof errorData.key === 'string' ? errorData.key : undefined,
    );
  }

  const text = await response.text();
  return text ? JSON.parse(text) : ({} as T);
}

export const api = {
  get: <T>(url: string, token?: string) => request<T>('GET', url, undefined, token),
  /** Spec 069 — a GET the caller can abort (dashboard blocks time out after 3 s). */
  getAbortable: <T>(url: string, token: string, signal: AbortSignal) => request<T>('GET', url, undefined, token, signal),
  post: <T>(url: string, data: unknown, token?: string) => request<T>('POST', url, data, token),
  put: <T>(url: string, data: unknown, token?: string) => request<T>('PUT', url, data, token),
  patch: <T>(url: string, data?: unknown, token?: string) => request<T>('PATCH', url, data, token),
  delete: <T>(url: string, token?: string) => request<T>('DELETE', url, undefined, token),

  // Garage admin endpoints
  listGarages: (
    token: string,
    params?: { status?: string; search?: string; pendingDocuments?: boolean; termsStatus?: string },
  ) => {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.search) query.set('search', params.search);
    if (params?.pendingDocuments) query.set('pendingDocuments', 'true');
    if (params?.termsStatus) query.set('termsStatus', params.termsStatus);
    const qs = query.toString();
    return request<import('../types').GaragesListResponse>('GET', `/api/garages${qs ? `?${qs}` : ''}`, undefined, token);
  },

  getGarageById: (id: string, token: string) =>
    request<import('../types').GarageDetailResponse>('GET', `/api/garages/${id}`, undefined, token),

  approveGarage: (id: string, token: string) =>
    request<import('../types').GarageResponse>('PATCH', `/api/garages/${id}/approve`, undefined, token),

  rejectGarage: (id: string, reason: string, token: string) =>
    request<import('../types').GarageResponse>('PATCH', `/api/garages/${id}/reject`, { reason }, token),

  requestGarageUpdate: (id: string, note: string, token: string) =>
    request<import('../types').GarageResponse>('PATCH', `/api/garages/${id}/request-update`, { note }, token),

  blockGarage: (id: string, token: string) =>
    request<import('../types').GarageResponse>('PATCH', `/api/garages/${id}/block`, undefined, token),

  unblockGarage: (id: string, token: string) =>
    request<import('../types').GarageResponse>('PATCH', `/api/garages/${id}/unblock`, undefined, token),

  getDownloadUrl: (garageId: string, storageKey: string, token: string) =>
    request<{ url: string }>('GET', `/api/garages/${garageId}/download-url?storageKey=${encodeURIComponent(storageKey)}`, undefined, token),

  listGarageDocuments: (garageId: string, token: string) =>
    request<import('../types').GarageDocumentResponse[]>('GET', `/api/garages/${garageId}/documents`, undefined, token),

  approveDocument: (documentId: string, token: string) =>
    request<import('../types').GarageDocumentResponse>('POST', `/api/garages/documents/${documentId}/approve`, undefined, token),

  rejectDocument: (documentId: string, reason: string, token: string) =>
    request<import('../types').GarageDocumentResponse>('POST', `/api/garages/documents/${documentId}/reject`, { reason }, token),

  // Service category endpoints (admin)
  listCategories: (activeOnly: boolean, token: string) =>
    request<import('../types').ServiceCategoryResponse[]>('GET', `/api/garages/categories?activeOnly=${activeOnly}`, undefined, token),

  createCategory: (data: import('../types').CreateServiceCategoryRequest, token: string) =>
    request<import('../types').ServiceCategoryResponse>('POST', '/api/garages/categories', data, token),

  updateCategory: (id: string, data: import('../types').CreateServiceCategoryRequest, token: string) =>
    request<import('../types').ServiceCategoryResponse>('PUT', `/api/garages/categories/${id}`, data, token),

  deactivateCategory: (id: string, token: string) =>
    request<void>('DELETE', `/api/garages/categories/${id}`, undefined, token),

  reactivateCategory: (id: string, token: string) =>
    request<import('../types').ServiceCategoryResponse>('PATCH', `/api/garages/categories/${id}/reactivate`, undefined, token),

  getCategoryUploadUrl: (
    id: string,
    data: { fileName: string; contentType: string; mediaType: 'CATEGORY_IMAGE'; fileSize: number },
    token: string,
  ) =>
    request<{ uploadUrl: string; storageKey: string }>(
      'POST',
      `/api/garages/categories/${id}/upload-url`,
      data,
      token,
    ),

  confirmCategoryUpload: (
    id: string,
    data: {
      storageKey: string;
      mediaType: 'CATEGORY_IMAGE';
      originalFilename: string;
      contentType: string;
      fileSize: number;
    },
    token: string,
  ) =>
    request<{ uploadUrl: string | null; storageKey: string }>(
      'POST',
      `/api/garages/categories/${id}/confirm-upload`,
      data,
      token,
    ),

  deleteCategoryImage: (id: string, token: string) =>
    request<void>('DELETE', `/api/garages/categories/${id}/image`, undefined, token),

  // Service offering admin endpoints
  listPendingApprovals: (token: string) =>
    request<import('../types').GaragePendingApprovalsResponse[]>('GET', '/api/garages/admin/pending-approvals', undefined, token),

  getGarageServices: (garageId: string, token: string) =>
    request<import('../types').GarageServiceOfferingResponse[]>('GET', `/api/garages/admin/${garageId}/services`, undefined, token),

  approveOffering: (offeringId: string, token: string) =>
    request<import('../types').GarageServiceOfferingResponse>('PATCH', `/api/garages/admin/offerings/${offeringId}/approve`, {}, token),

  getOfferingDetail: (offeringId: string, token: string) =>
    request<import('../types').GarageServiceOfferingResponse>('GET', `/api/garages/admin/offerings/${offeringId}`, undefined, token),

  rejectOffering: (offeringId: string, reason: string, token: string) =>
    request<void>('PATCH', `/api/garages/admin/offerings/${offeringId}/reject`, { reason }, token),

  changeOfferingStatus: (offeringId: string, status: 'ACTIVE' | 'INACTIVE', token: string) =>
    request<import('../types').GarageServiceOfferingResponse>('PATCH', `/api/garages/admin/offerings/${offeringId}/status`, { status }, token),

  blockOffering: (offeringId: string, token: string) =>
    request<import('../types').GarageServiceOfferingResponse>('PATCH', `/api/garages/admin/offerings/${offeringId}/block`, undefined, token),

  unblockOffering: (offeringId: string, token: string) =>
    request<import('../types').GarageServiceOfferingResponse>('PATCH', `/api/garages/admin/offerings/${offeringId}/unblock`, undefined, token),

  // Spec 044 v2: variant-level review endpoints
  listPendingVariants: (offeringId: string, token: string) =>
    request<import('../types').PendingVariantsResponse>('GET', `/api/garages/admin/offerings/${offeringId}/pending-variants`, undefined, token),

  approveVariant: (offeringId: string, variantId: string, token: string) =>
    request<void>('POST', `/api/garages/admin/offerings/${offeringId}/variants/${variantId}/approve`, {}, token),

  rejectVariant: (offeringId: string, variantId: string, reason: string, token: string) =>
    request<void>('POST', `/api/garages/admin/offerings/${offeringId}/variants/${variantId}/reject`, { reason }, token),

  requestVariantChanges: (offeringId: string, variantId: string, reason: string, token: string) =>
    request<void>('POST', `/api/garages/admin/offerings/${offeringId}/variants/${variantId}/request-changes`, { reason }, token),

  approveVariantsBulk: (offeringId: string, variantIds: string[], token: string) =>
    request<void>('POST', `/api/garages/admin/offerings/${offeringId}/variants/approve-bulk`, { variantIds }, token),

  // Spec 062 — garage offers review (Service approvals → Offers tab; screen `service-approvals`)
  listOfferQueue: (token: string, status: import('../types/offers').OfferStatus = 'PENDING_APPROVAL', page = 0, size = 20) => {
    const query = new URLSearchParams({ status, page: String(page), size: String(size) });
    return request<import('../types').PageResponse<import('../types/offers').AdminOfferRow>>(
      'GET', `/api/garages/admin/offers?${query.toString()}`, undefined, token);
  },

  getOfferForReview: (offerId: string, token: string) =>
    request<import('../types/offers').AdminOfferDetail>('GET', `/api/garages/admin/offers/${offerId}`, undefined, token),

  /** Decision "4 preview": per-variant prices of the offer (no body = its own rule). Read-only. */
  previewOffer: (offerId: string, token: string, body?: import('../types/offers').OfferPreviewRequest) =>
    request<import('../types/offers').OfferPreviewResponse>('POST', `/api/garages/admin/offers/${offerId}/preview`, body, token),

  approveOffer: (offerId: string, body: import('../types/offers').ApproveOfferRequest, token: string) =>
    request<import('../types/offers').OfferResponse>('PATCH', `/api/garages/admin/offers/${offerId}/approve`, body, token),

  rejectOffer: (offerId: string, body: import('../types/offers').RejectOfferRequest, token: string) =>
    request<import('../types/offers').OfferResponse>('PATCH', `/api/garages/admin/offers/${offerId}/reject`, body, token),

  suspendOffer: (offerId: string, body: import('../types/offers').SuspendOfferRequest, token: string) =>
    request<import('../types/offers').OfferResponse>('PATCH', `/api/garages/admin/offers/${offerId}/suspend`, body, token),

  // #997 — admins edit a garage offer (keeps its status; the garage gets an OFFER_EDITED feed row)
  updateOfferAsAdmin: (offerId: string, body: import('../types/offers').AdminOfferUpdateRequest, token: string) =>
    request<import('../types/offers').OfferResponse>('PUT', `/api/garages/admin/offers/${offerId}`, body, token),

  getOfferBannerUploadUrl: (offerId: string, body: import('../types/offers').OfferBannerUploadUrlRequest, token: string) =>
    request<import('../types/offers').OfferBannerUploadUrlResponse>(
      'POST', `/api/garages/admin/offers/${offerId}/banner/upload-url`, body, token),

  getOfferHistory: (offerId: string, token: string, limit = 50) =>
    request<import('../types/offers').OfferHistoryEntry[]>(
      'GET', `/api/garages/admin/offers/${offerId}/history?limit=${limit}`, undefined, token),

  /** Browser → S3 PUT of a banner to the presigned `uploadUrl` (no auth header; no confirm call). */
  putOfferBannerToStorage: async (uploadUrl: string, file: Blob, contentType: string): Promise<void> => {
    const res = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
    if (!res.ok) throw new ApiError(`Upload failed (HTTP ${res.status})`, res.status);
  },

  // Spec 081b — partner packages review (Service approvals → Packages tab; screen `service-approvals`)
  listPackageQueue: (token: string, params: import('../types/packages').PackageListParams = {}) => {
    const query = new URLSearchParams({
      status: params.status ?? 'PENDING_APPROVAL',
      page: String(params.page ?? 0),
      size: String(params.size ?? 20),
    });
    if (params.garageId) query.set('garageId', params.garageId);
    return request<import('../types').PageResponse<import('../types/packages').AdminPackageRow>>(
      'GET', `/api/garages/admin/packages?${query.toString()}`, undefined, token);
  },

  /** Normalised by `toAdminPackageDetail` (flat PackageResponse + garage, or the package nested). */
  getPackageForReview: async (packageId: string, token: string) =>
    toAdminPackageDetail(await request<Parameters<typeof toAdminPackageDetail>[0]>(
      'GET', `/api/garages/admin/packages/${packageId}`, undefined, token)),

  approvePackage: (packageId: string, body: import('../types/packages').ApprovePackageRequest, token: string) =>
    request<import('../types/packages').PackageResponse>('PATCH', `/api/garages/admin/packages/${packageId}/approve`, body, token),

  rejectPackage: (packageId: string, body: import('../types/packages').RejectPackageRequest, token: string) =>
    request<import('../types/packages').PackageResponse>('PATCH', `/api/garages/admin/packages/${packageId}/reject`, body, token),

  suspendPackage: (packageId: string, body: import('../types/packages').SuspendPackageRequest, token: string) =>
    request<import('../types/packages').PackageResponse>('PATCH', `/api/garages/admin/packages/${packageId}/suspend`, body, token),

  /** Admin edit in place: status kept, `version` required (409 PACKAGE_STATE_CHANGED when stale, ENDED → 409). */
  updatePackageAsAdmin: (packageId: string, body: import('../types/packages').PackageRequest, token: string) =>
    request<import('../types/packages').PackageResponse>('PUT', `/api/garages/admin/packages/${packageId}`, body, token),

  getPackageHistory: (packageId: string, token: string) =>
    request<import('../types/packages').PackageHistoryEntry[]>(
      'GET', `/api/garages/admin/packages/${packageId}/history`, undefined, token),

  /** The admin garage page's Packages tab: every package of the garage (hidden ones excluded). */
  listGaragePackages: (garageId: string, token: string) =>
    request<import('../types/packages').AdminPackageRow[]>(
      'GET', `/api/garages/admin/garages/${garageId}/packages`, undefined, token),

  // Translation admin endpoints
  listTranslations: (token: string, params?: { search?: string; context?: string; missingArabic?: boolean; page?: number; size?: number }) => {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.context) query.set('context', params.context);
    if (params?.missingArabic) query.set('missingArabic', 'true');
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.size !== undefined) query.set('size', String(params.size));
    const qs = query.toString();
    return request<import('../types').PageResponse<import('../types').TranslationEntry>>('GET', `/api/admin/translations${qs ? `?${qs}` : ''}`, undefined, token);
  },

  createTranslation: (data: import('../types').CreateTranslationRequest, token: string) =>
    request<import('../types').TranslationEntry>('POST', '/api/admin/translations', data, token),

  updateTranslation: (id: string, data: import('../types').UpdateTranslationRequest, token: string) =>
    request<import('../types').TranslationEntry>('PUT', `/api/admin/translations/${id}`, data, token),

  deleteTranslation: (id: string, token: string) =>
    request<void>('DELETE', `/api/admin/translations/${id}`, undefined, token),

  bulkImportTranslations: (data: import('../types').BulkImportRequest, token: string) =>
    request<import('../types').BulkImportResult>('POST', '/api/admin/translations/bulk', data, token),

  // Lookup admin endpoints
  fetchAdminCategories: (token: string) =>
    request<import('../types').LookupCategoryResponse[]>('GET', '/api/admin/lookups/categories', undefined, token),

  createLookupEntry: (categoryId: string, data: { code: string; name: string; nameAr?: string; parentEntryId?: string; sortOrder?: number }, token: string) =>
    request<import('../types').LookupEntryResponse>('POST', `/api/admin/lookups/categories/${categoryId}/entries`, data, token),

  updateLookupEntry: (entryId: string, data: { name?: string; nameAr?: string; sortOrder?: number; parentEntryId?: string }, token: string) =>
    request<import('../types').LookupEntryResponse>('PUT', `/api/admin/lookups/entries/${entryId}`, data, token),

  deactivateLookupEntry: (entryId: string, token: string) =>
    request<void>('PATCH', `/api/admin/lookups/entries/${entryId}/status`, { isActive: false }, token),

  activateLookupEntry: (entryId: string, token: string) =>
    request<void>('PATCH', `/api/admin/lookups/entries/${entryId}/status`, { isActive: true }, token),

  reorderLookupEntries: (categoryId: string, entryIds: string[], token: string) =>
    request<void>('PUT', `/api/admin/lookups/categories/${categoryId}/entries/reorder`, { orderedIds: entryIds }, token),

  // Service proposal admin endpoints
  fetchProposals: (status: string | undefined, token: string) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const qs = params.toString();
    return request<import('../types').ServiceProposalResponse[]>('GET', `/api/admin/service-proposals${qs ? `?${qs}` : ''}`, undefined, token);
  },

  approveProposal: (proposalId: string, data: { approvedCategoryId?: string }, token: string) =>
    request<import('../types').ServiceProposalResponse>('POST', `/api/admin/service-proposals/${proposalId}/approve`, data, token),

  rejectProposal: (proposalId: string, reason: string, token: string) =>
    request<import('../types').ServiceProposalResponse>('POST', `/api/admin/service-proposals/${proposalId}/reject`, { reason }, token),

  fetchProposalsByGarage: (garageId: string, token: string) =>
    request<import('../types').ServiceProposalResponse[]>('GET', `/api/admin/service-proposals/garage/${garageId}`, undefined, token),

  // Terms document admin endpoints — Spec 043 adds audience filter + version history
  getTermsDocuments: (token: string, audience?: import('../types').TermsAudience) =>
    request<import('../types').TermsDocumentResponse[]>(
      'GET',
      audience
        ? `/api/admin/terms-documents?audience=${encodeURIComponent(audience)}`
        : '/api/admin/terms-documents',
      undefined,
      token,
    ),

  createTermsDocument: (data: import('../types').CreateTermsDocumentRequest, token: string) =>
    request<import('../types').TermsDocumentResponse>('POST', '/api/admin/terms-documents', data, token),

  updateTermsDocument: (id: string, data: import('../types').UpdateTermsDocumentRequest, token: string) =>
    request<import('../types').TermsDocumentResponse>('PUT', `/api/admin/terms-documents/${id}`, data, token),

  updateTermsDocumentStatus: (id: string, isActive: boolean, token: string) =>
    request<import('../types').TermsDocumentResponse>('PATCH', `/api/admin/terms-documents/${id}/status`, { isActive }, token),

  reorderTermsDocuments: (documentIds: string[], token: string) =>
    request<void>('PUT', '/api/admin/terms-documents/reorder', { documentIds }, token),

  listTermsDocumentVersions: (id: string, token: string) =>
    request<import('../types').TermsDocumentVersionResponse[]>(
      'GET',
      `/api/admin/terms-documents/${id}/versions`,
      undefined,
      token,
    ),

  // Spec 071 — list: gateway screen `garages`; download: screen `signed-agreements` (409 PDF_NOT_READY).
  getGarageSignedAgreements: (garageId: string, token: string) =>
    request<import('../types').AdminSignedAgreement[]>(
      'GET', `/api/garages/admin/garages/${encodeURIComponent(garageId)}/signed-agreements`, undefined, token),

  getSignedAgreementDownloadUrl: (recordId: string, token: string) =>
    request<import('../types').SignedAgreementDownloadUrl>(
      'GET', `/api/garages/admin/signed-agreements/${encodeURIComponent(recordId)}/download-url`, undefined, token),

  // Spec 074 — terms update notices. Reads: screen `garages`; sends: screen `signed-agreements`.
  getGarageTermsStatus: (garageId: string, token: string) =>
    request<import('../types').GarageTermsStatus>(
      'GET', `/api/garages/admin/garages/${encodeURIComponent(garageId)}/terms-status`, undefined, token),

  sendGarageTermsNotice: (garageId: string, token: string) =>
    request<import('../types').TermsNoticeSendResult>(
      'POST', `/api/garages/admin/garages/${encodeURIComponent(garageId)}/terms-notices`, undefined, token),

  getTermsNoticeCandidates: (token: string) =>
    request<import('../types').TermsNoticeCandidates>(
      'GET', '/api/garages/admin/terms-notices/candidates', undefined, token),

  /** Spec 074 v2 — at most TERMS_NOTICE_SEND_CHUNK (100) ids per call; the dialog chunks larger picks. */
  sendTermsNoticesTo: (garageIds: string[], token: string) =>
    request<import('../types').TermsNoticeSendSelectedResult>(
      'POST', '/api/garages/admin/terms-notices/send', { garageIds }, token),

  getGarageMembers: (garageId: string, token: string) =>
    request<import('../types').GarageMemberResponse[]>('GET', `/api/garages/${garageId}/members`, undefined, token),

  // Admin Identity — screen roles and admin provisioning (spec 050)
  listAdminsWithRoles: (token: string, search?: string) => {
    const query = new URLSearchParams();
    if (search) query.set('search', search);
    const qs = query.toString();
    return request<import('../types/admin-roles').AdminUserWithRoles[]>(
      'GET', `/api/admin/identity/admins${qs ? `?${qs}` : ''}`, undefined, token);
  },

  listAdminScreens: (token: string) =>
    request<import('../types/admin-roles').AdminScreen[]>(
      'GET', '/api/admin/identity/screens', undefined, token),

  listAdminRoles: (token: string) =>
    request<import('../types/admin-roles').AdminRole[]>(
      'GET', '/api/admin/identity/roles', undefined, token),

  createAdminRole: (
    body: import('../types/admin-roles').SaveAdminRoleRequest,
    token: string,
  ) =>
    request<import('../types/admin-roles').AdminRole>(
      'POST', '/api/admin/identity/roles', body, token),

  updateAdminRole: (
    roleId: string,
    body: import('../types/admin-roles').SaveAdminRoleRequest,
    token: string,
  ) =>
    request<import('../types/admin-roles').AdminRole>(
      'PUT', `/api/admin/identity/roles/${roleId}`, body, token),

  deleteAdminRole: (roleId: string, token: string) =>
    request<void>('DELETE', `/api/admin/identity/roles/${roleId}`, undefined, token),

  createAdmin: (
    body: import('../types/admin-roles').CreateAdminRequest,
    token: string,
  ) =>
    request<import('../types/admin-roles').AdminUserWithRoles>(
      'POST', '/api/admin/identity/admins', body, token),

  updateAdminAssignments: (
    subjectId: string,
    body: import('../types/admin-roles').UpdateAdminAssignmentsRequest,
    token: string,
  ) =>
    request<import('../types/admin-roles').AdminUserWithRoles>(
      'PUT', `/api/admin/identity/admins/${subjectId}/roles`, body, token),

  // #954 — super-admin staff-account actions. Both target the Keycloak subject id.
  deleteAdmin: (subjectId: string, token: string) =>
    request<void>('DELETE', `/api/admin/identity/admins/${subjectId}`, undefined, token),

  sendAdminPasswordReset: (subjectId: string, token: string) =>
    request<void>('POST', `/api/admin/identity/admins/${subjectId}/password-reset`, undefined, token),

  // Admin Garage Edit — spec 038
  adminGarageUpdateProfile: (
    garageId: string,
    body: import('../types/admin-garage-edit').AdminUpdateProfileRequest,
    token: string,
  ) => request<import('../types').GarageResponse>(
    'PUT', `/api/garages/admin/${garageId}/profile`, body, token),

  // Spec 058 — Google listing. Admin-only; the search is proxied through the backend so the
  // Places-enabled key never ships in this bundle.
  adminGarageGetGoogleListing: (garageId: string, token: string) =>
    request<import('../types/admin-garage-edit').GoogleListingResponse>(
      'GET', `/api/garages/admin/${garageId}/google-listing`, undefined, token),

  adminGarageSearchGoogleListing: (garageId: string, q: string, token: string, radiusKm?: number) =>
    request<import('../types/admin-garage-edit').GoogleListingSearchResponse>(
      'GET',
      `/api/garages/admin/${garageId}/google-listing/search?q=${encodeURIComponent(q)}`
        + (radiusKm != null ? `&radiusKm=${radiusKm}` : ''),
      undefined, token),

  adminGarageLinkGoogleListing: (
    garageId: string,
    body: import('../types/admin-garage-edit').LinkGoogleListingRequest,
    token: string,
  ) => request<import('../types/admin-garage-edit').GoogleListingResponse>(
    'PUT', `/api/garages/admin/${garageId}/google-listing`, body, token),

  adminGarageUnlinkGoogleListing: (garageId: string, token: string) =>
    request<void>('DELETE', `/api/garages/admin/${garageId}/google-listing`, undefined, token),

  adminGarageUpdateHours: (
    garageId: string,
    body: import('../types/admin-garage-edit').AdminSetHoursRequest[],
    token: string,
  ) => request<unknown[]>(
    'PUT', `/api/garages/admin/${garageId}/hours`, body, token),

  /** #1385 — allDayEnabled: the Runtime Config switch for 24-hour days (off until the app shows them). */
  adminGarageHoursSettings: (garageId: string, token: string) =>
    request<{ allDayEnabled: boolean }>('GET', `/api/garages/admin/${garageId}/hours/settings`, undefined, token),

  adminGarageAddMember: (
    garageId: string,
    body: import('../types/admin-garage-edit').AdminAddMemberRequest,
    token: string,
  ) => request<import('../types').GarageMemberResponse>(
    'POST', `/api/garages/admin/${garageId}/members`, body, token),

  adminGarageRemoveMember: (garageId: string, memberId: string, token: string) =>
    request<void>('DELETE', `/api/garages/admin/${garageId}/members/${memberId}`, undefined, token),

  adminGarageUpdateBankDetails: (
    garageId: string,
    body: import('../types/admin-garage-edit').AdminUpdateBankDetailsRequest,
    token: string,
  ) => request<import('../types/admin-garage-edit').AdminBankDetailsResponse>(
    'PUT', `/api/garages/admin/${garageId}/bank-details`, body, token),

  adminGarageUpdateVat: (
    garageId: string,
    body: import('../types/admin-garage-edit').AdminUpdateVatRequest,
    token: string,
  ) => request<import('../types/admin-garage-edit').AdminVatResponse>(
    'PUT', `/api/garages/admin/${garageId}/vat`, body, token),

  adminGarageUpdateOffering: (
    garageId: string,
    offeringId: string,
    body: import('../types/admin-garage-edit').AdminUpdateOfferingRequest,
    token: string,
  ) => request<import('../types').GarageServiceOfferingResponse>(
    'PATCH', `/api/garages/admin/${garageId}/offerings/${offeringId}`, body, token),

  adminGarageCreateOffering: (
    garageId: string,
    body: import('../types/admin-garage-edit').AdminCreateOfferingRequest,
    token: string,
  ) => request<import('../types').GarageServiceOfferingResponse>(
    'POST', `/api/garages/admin/${garageId}/offerings`, body, token),

  adminGarageMediaUploadUrl: (
    garageId: string,
    body: import('../types/admin-garage-edit').AdminMediaUploadUrlRequest,
    token: string,
  ) => request<{ uploadUrl: string; storageKey: string }>(
    'POST', `/api/garages/admin/${garageId}/media/upload-url`, body, token),

  adminGarageMediaConfirm: (
    garageId: string,
    body: import('../types/admin-garage-edit').AdminConfirmUploadRequest,
    token: string,
  ) => request<unknown>('POST', `/api/garages/admin/${garageId}/media/confirm`, body, token),

  adminGarageMediaDelete: (garageId: string, mediaId: string, token: string) =>
    request<void>('DELETE', `/api/garages/admin/${garageId}/media/${mediaId}`, undefined, token),

  adminGarageDocumentUpload: (
    garageId: string,
    documentType: string,
    body: import('../types/admin-garage-edit').AdminConfirmUploadRequest,
    token: string,
  ) => request<import('../types').GarageDocumentResponse>(
    'POST', `/api/garages/admin/${garageId}/documents?documentType=${encodeURIComponent(documentType)}`, body, token),

  adminGarageOfferingMediaUploadUrl: (
    garageId: string,
    offeringId: string,
    body: { mediaType: 'SERVICE_THUMBNAIL' | 'SERVICE_PHOTO'; fileName: string; contentType: string; fileSize?: number },
    token: string,
  ) => request<{ uploadUrl: string; storageKey: string }>(
    'POST', `/api/garages/admin/${garageId}/offerings/${offeringId}/upload-url`, body, token),

  adminGarageOfferingMediaConfirm: (
    garageId: string,
    offeringId: string,
    body: { mediaType: 'SERVICE_THUMBNAIL' | 'SERVICE_PHOTO'; storageKey: string; originalFilename?: string; contentType?: string; fileSize?: number },
    token: string,
  ) => request<{ mediaId: string; downloadUrl: string; mediaType: string }>(
    'POST', `/api/garages/admin/${garageId}/offerings/${offeringId}/confirm-upload`, body, token),

  // The offering response carries bare photo URLs; this is where mediaId lives, and the UI
  // needs it to delete or replace a saved image.
  adminGarageOfferingMediaList: (garageId: string, offeringId: string, token: string) =>
    request<{ mediaId: string; mediaType: string; storageKey: string; downloadUrl: string; originalFilename: string | null; sortOrder: number | null }[]>(
      'GET', `/api/garages/admin/${garageId}/offerings/${offeringId}/media`, undefined, token),

  adminGarageOfferingMediaDelete: (garageId: string, offeringId: string, mediaId: string, token: string) =>
    request<void>('DELETE', `/api/garages/admin/${garageId}/offerings/${offeringId}/media/${mediaId}`, undefined, token),

  /** Admin view of a garage's media — includes documents, and the only source of mediaId. */
  adminGarageMediaList: (garageId: string, token: string) =>
    request<import('../types').MediaResponse[]>(
      'GET', `/api/garages/admin/${garageId}/media`, undefined, token),

  /**
   * Set the order a garage's gallery photos appear in. Send every gallery-photo id in the
   * intended order; the logo is the cover image and its id is rejected.
   */
  adminReorderGarageMedia: (garageId: string, orderedIds: string[], token: string) =>
    request<import('../types').MediaResponse[]>(
      'PUT', `/api/garages/admin/${garageId}/media/reorder`, { orderedIds }, token),

  /**
   * Set the order a garage's services appear in on the customer-facing garage profile. Send
   * every service id in the intended order — the server assigns positions and rejects a partial
   * list. Does not change any service's approval status.
   */
  adminReorderOfferings: (garageId: string, orderedIds: string[], token: string) =>
    request<import('../types').GarageServiceOfferingResponse[]>(
      'PUT', `/api/garages/admin/${garageId}/offerings/reorder`, { orderedIds }, token),

  /** Same contract, for one service's gallery photos. The thumbnail is not included. */
  adminReorderOfferingMedia: (garageId: string, offeringId: string, orderedIds: string[], token: string) =>
    request<{ mediaId: string; mediaType: string; storageKey: string; downloadUrl: string; originalFilename: string | null; sortOrder: number | null }[]>(
      'PUT', `/api/garages/admin/${garageId}/offerings/${offeringId}/media/reorder`, { orderedIds }, token),

  fetchLookupEntries: (categoryCode: string, parentId: string | undefined, token: string) => {
    const qs = parentId ? `?parentId=${encodeURIComponent(parentId)}&activeOnly=true` : '?activeOnly=true';
    return request<import('../types/admin-garage-edit').LookupEntry[]>(
      'GET', `/api/lookups/categories/${categoryCode}/entries${qs}`, undefined, token);
  },

  // Admin Orders (F1 #162 — Phase 13)
  listAdminOrders: (params: {
    status?: string;
    garageId?: string;
    customerId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    size?: number;
  }, token: string) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    });
    return request<PagedAdminOrders>('GET', `/api/admin/orders?${qs.toString()}`, undefined, token);
  },

  cancelAdminOrder: (orderId: string, reason: string, token: string) =>
    request<AdminOrderDetail>('PUT', `/api/admin/orders/${orderId}/cancel`, { reason }, token),

  getAdminOrderDetail: (orderId: string, token: string) =>
    request<AdminOrderDetail>('GET', `/api/admin/orders/${orderId}`, undefined, token),

  // Pickup Time Slots (F3 #164)
  listPickupTimeSlots: (includeInactive: boolean, token: string) =>
    request<import('../types').PickupTimeSlot[]>(
      'GET', `/api/admin/pickup-time-slots?includeInactive=${includeInactive}`, undefined, token),

  createPickupTimeSlot: (body: import('../types').CreatePickupTimeSlotRequest, token: string) =>
    request<import('../types').PickupTimeSlot>('POST', '/api/admin/pickup-time-slots', body, token),

  updatePickupTimeSlot: (id: string, body: import('../types').UpdatePickupTimeSlotRequest, token: string) =>
    request<import('../types').PickupTimeSlot>('PUT', `/api/admin/pickup-time-slots/${id}`, body, token),

  deletePickupTimeSlot: (id: string, token: string) =>
    request<void>('DELETE', `/api/admin/pickup-time-slots/${id}`, undefined, token),

  // Spec 045: Admin V2 offering write endpoints (auto-approve — everything lands ACTIVE)
  createOfferingV2Admin: (garageId: string, payload: import('../types/offeringsV2').CreateOfferingV2Request, token: string) =>
    request<import('../types/offeringsV2').OfferingV2Response>('POST', `/api/garages/admin/garages/${garageId}/services-v2`, payload, token),

  getAdminOfferingV2: (garageId: string, offeringId: string, token: string) =>
    request<import('../types/offeringsV2').OfferingV2Response>('GET', `/api/garages/admin/garages/${garageId}/services-v2/${offeringId}`, undefined, token),

  updateOfferingV2Admin: (garageId: string, offeringId: string, payload: import('../types/offeringsV2').UpdateOfferingV2Request, token: string) =>
    request<import('../types/offeringsV2').OfferingV2Response>('PUT', `/api/garages/admin/garages/${garageId}/services-v2/${offeringId}`, payload, token),

  replaceVariationsAdmin: (garageId: string, offeringId: string, payload: import('../types/offeringsV2').ReplaceVariationsRequest, token: string) =>
    request<import('../types/offeringsV2').OfferingV2Response>('PUT', `/api/garages/admin/garages/${garageId}/services-v2/${offeringId}/variations`, payload, token),

  addCarToOfferingAdmin: (garageId: string, offeringId: string, payload: import('../types/offeringsV2').SupportedCarPayload, token: string) =>
    request<import('../types/offeringsV2').OfferingV2Response>('POST', `/api/garages/admin/garages/${garageId}/services-v2/${offeringId}/cars`, payload, token),

  removeCarFromOfferingAdmin: (garageId: string, offeringId: string, carId: string, token: string) =>
    request<void>('DELETE', `/api/garages/admin/garages/${garageId}/services-v2/${offeringId}/cars/${carId}`, undefined, token),

  upsertCarVariantsAdmin: (garageId: string, offeringId: string, carId: string, payload: import('../types/offeringsV2').UpsertCarVariantsRequest, token: string) =>
    request<import('../types/offeringsV2').OfferingV2Response>('POST', `/api/garages/admin/garages/${garageId}/services-v2/${offeringId}/cars/${carId}/variants`, payload, token),

  // Replaces the per-car loop: one request, one transaction, only the cars that changed.
  // The old loop issued one call per car (467 for Luxury Wheels), took 10-21 minutes, and
  // outlived the 15-minute access token — dying half-applied with a 401.
  upsertCarVariantsBulkAdmin: (garageId: string, offeringId: string, payload: import('../types/offeringsV2').BulkUpsertCarVariantsRequest, token: string) =>
    request<import('../types/offeringsV2').BulkUpsertResult>('POST', `/api/garages/admin/garages/${garageId}/services-v2/${offeringId}/cars/variants-bulk`, payload, token),

  upsertServiceVariantsAdmin: (garageId: string, offeringId: string, payload: import('../types/offeringsV2').UpsertServiceVariantsRequest, token: string) =>
    request<import('../types/offeringsV2').OfferingV2Response>('PUT', `/api/garages/admin/garages/${garageId}/services-v2/${offeringId}/service-variants`, payload, token),

  // Spec 046: admin action history for a specific offering
  getOfferingHistory: (offeringId: string, token: string, limit = 50) =>
    request<import('../types/offeringsV2').AdminActionView[]>('GET', `/api/garages/admin/offerings/${offeringId}/history?limit=${limit}`, undefined, token),
};
